/**
 * syncWorker — drains the offline queue when connectivity returns.
 *
 * One-at-a-time replay with exponential backoff on failure. After the
 * configured max retries the entry is marked `failed` and left in the queue;
 * a human or a future "retry now" UI can flip it back to `pending`.
 *
 * Emits `phd:sync-completed` on `window` after each drain attempt; payload is
 * `{ successCount, failedCount }`.
 */
import { QueryClient } from "@tanstack/react-query";
import {
  dequeue,
  markFailed,
  markInFlight,
  markPending,
  peekAll,
  type OfflineEntry,
} from "./offlineQueue";

export const RETRY_DELAYS_MS = [1_000, 5_000, 30_000, 300_000, 3_600_000];
export const MAX_ATTEMPTS = RETRY_DELAYS_MS.length;

export interface SyncCompletedDetail {
  successCount: number;
  failedCount: number;
}

export const SYNC_COMPLETED_EVENT = "phd:sync-completed";

export interface SyncWorkerOptions {
  queryClient: QueryClient;
  getAccessToken: () => string | null;
  /** Default fetch — overrideable for tests. */
  fetcher?: typeof fetch;
  /** Wait between retries; overridable for tests. */
  sleep?: (ms: number) => Promise<void>;
  /** Per-entry invalidation hook (e.g. invalidate the relevant query). */
  onEntrySynced?: (entry: OfflineEntry, response: Response) => void | Promise<void>;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

export class SyncWorker {
  private readonly opts: Required<
    Omit<SyncWorkerOptions, "onEntrySynced">
  > & { onEntrySynced?: SyncWorkerOptions["onEntrySynced"] };
  private running = false;

  constructor(options: SyncWorkerOptions) {
    this.opts = {
      queryClient: options.queryClient,
      getAccessToken: options.getAccessToken,
      fetcher: options.fetcher ?? fetch.bind(globalThis),
      sleep: options.sleep ?? defaultSleep,
      onEntrySynced: options.onEntrySynced,
    };
  }

  /**
   * Drain the queue. Safe to call concurrently — only one drain runs at a time;
   * concurrent calls become no-ops.
   */
  async drain(): Promise<SyncCompletedDetail> {
    if (this.running) return { successCount: 0, failedCount: 0 };
    this.running = true;
    let successCount = 0;
    let failedCount = 0;
    try {
      const entries = await peekAll();
      for (const entry of entries) {
        if (entry.status === "failed") continue;
        const result = await this.replayWithBackoff(entry);
        if (result === "synced") successCount += 1;
        else failedCount += 1;
      }
    } finally {
      this.running = false;
      this.emit({ successCount, failedCount });
    }
    return { successCount, failedCount };
  }

  private emit(detail: SyncCompletedDetail) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent<SyncCompletedDetail>(SYNC_COMPLETED_EVENT, { detail }));
  }

  private async replayWithBackoff(
    entry: OfflineEntry,
  ): Promise<"synced" | "failed"> {
    let attempt = entry.attempts;
    let lastError: unknown = null;
    while (attempt < MAX_ATTEMPTS) {
      const delay = attempt === 0 ? 0 : RETRY_DELAYS_MS[attempt - 1] ?? 0;
      if (delay > 0) await this.opts.sleep(delay);
      try {
        await markInFlight(entry.id);
        const response = await this.send(entry);
        if (response.ok) {
          if (this.opts.onEntrySynced) {
            await this.opts.onEntrySynced(entry, response);
          }
          await this.invalidate(entry);
          await dequeue(entry.id);
          return "synced";
        }
        // 4xx (other than 408/429) are non-retriable.
        if (
          response.status >= 400 &&
          response.status < 500 &&
          response.status !== 408 &&
          response.status !== 429
        ) {
          await markFailed(entry.id, `HTTP ${response.status}`);
          return "failed";
        }
        lastError = new Error(`HTTP ${response.status}`);
      } catch (err) {
        lastError = err;
        await markPending(entry.id);
      }
      attempt += 1;
    }
    const message = lastError instanceof Error ? lastError.message : String(lastError ?? "unknown");
    await markFailed(entry.id, message);
    return "failed";
  }

  private async send(entry: OfflineEntry): Promise<Response> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "Idempotency-Key": entry.idempotencyKey,
    };
    const token = this.opts.getAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const init: RequestInit = {
      method: entry.method,
      headers,
      credentials: "include",
    };
    if (entry.method !== "DELETE") {
      init.body = JSON.stringify(entry.body ?? {});
    }
    return this.opts.fetcher(entry.endpoint, init);
  }

  private async invalidate(entry: OfflineEntry): Promise<void> {
    // Heuristic cache invalidation by endpoint prefix.
    const key = endpointToQueryKey(entry.endpoint);
    if (key) await this.opts.queryClient.invalidateQueries({ queryKey: key });
  }
}

export function endpointToQueryKey(endpoint: string): string[] | null {
  // /api/v1/foo/bar/ → ["foo", "bar"]
  const match = endpoint.match(/\/api\/(?:v\d+\/)?(.+?)\/?$/);
  if (!match || !match[1]) return null;
  const segments = match[1].split("/").filter(Boolean);
  return segments.length > 0 ? segments : null;
}
